'use client';

import React, { useState } from 'react';
import { colors, formatNum, formatUSD, formatSmartPrice } from './SharedUI';

export default function LiquidationSqueezeRadar({ liveData, smartMoneyRef, histDataList, spotDataList, sharedZones }) {
  const [squeezeData, setSqueezeData] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  
  const [baselineId, setBaselineId] = useState('4h');
  const [useSpotData, setUseSpotData] = useState(true);

  const calcPnL = (isLong, qty, avgPrice, currentPrice) => {
    if (!currentPrice || !avgPrice || !qty) return 0;
    return isLong ? qty * (currentPrice - avgPrice) : qty * (avgPrice - currentPrice);
  };

  const analyzeSqueeze = () => {
    setIsAnalyzing(true);
    
    setTimeout(() => {
      const sm = smartMoneyRef?.current;
      if (!liveData || !sm || !histDataList || histDataList.length === 0) {
        alert("⚠️ Vui lòng đảm bảo đã có dữ liệu Live, Smart Money và Fetch đủ Lịch sử!");
        setIsAnalyzing(false);
        return;
      }

      const livePrice = liveData.last;
      const frLive = liveData.fundingRate || 0;
      
      const histTarget = histDataList.find(d => d.id === baselineId) || histDataList[0];
      const spotTarget = spotDataList?.find(d => d.id === baselineId) || spotDataList?.[0];

      const spotCvd = (useSpotData && spotTarget) ? (spotTarget.liveCvd || 0) : 0; 
      
      const liveOI = liveData.oiContract;
      const histOI = histTarget.oiContract !== "N/A" ? histTarget.oiContract : liveOI;
      const oiDeltaPct = histOI > 0 ? ((liveOI - histOI) / histOI) * 100 : 0;

      // 1. Phân tích PHE LONG & Tính PnL
      const longAvg = sm.longTradersAvgEntryPrice;
      const longDistPct = longAvg > 0 ? ((livePrice - longAvg) / longAvg) * 100 : 0; 
      const isLongTrapped = longDistPct < 0;
      
      const longTotalPnL = calcPnL(true, sm.longTradersQty, longAvg, livePrice);
      const longWhalePnL = calcPnL(true, sm.longWhalesQty, sm.longWhalesAvgEntryPrice, livePrice);
      const longRetailPnL = longTotalPnL - longWhalePnL;

      const distToSup = sharedZones?.sup ? ((livePrice - sharedZones.sup) / livePrice) * 100 : null;
      
      let longPainScore = 0;
      if (isLongTrapped) {
        if (useSpotData) {
          longPainScore += Math.min(Math.abs(longDistPct) * 10, 30); 
          if (oiDeltaPct > 0) longPainScore += Math.min(oiDeltaPct * 2, 20); 
          if (frLive > 0.0001) longPainScore += 20; 
          if (spotCvd < 0) longPainScore += 30; 
        } else {
          longPainScore += Math.min(Math.abs(longDistPct) * 15, 40); 
          if (oiDeltaPct > 0) longPainScore += Math.min(oiDeltaPct * 3, 30); 
          if (frLive > 0.0001) longPainScore += 30; 
        }
      }

      // 2. Phân tích PHE SHORT & Tính PnL
      const shortAvg = sm.shortTradersAvgEntryPrice;
      const shortDistPct = shortAvg > 0 ? ((shortAvg - livePrice) / shortAvg) * 100 : 0; 
      const isShortTrapped = shortDistPct < 0;

      const shortTotalPnL = calcPnL(false, sm.shortTradersQty, shortAvg, livePrice);
      const shortWhalePnL = calcPnL(false, sm.shortWhalesQty, sm.shortWhalesAvgEntryPrice, livePrice);
      const shortRetailPnL = shortTotalPnL - shortWhalePnL;

      const distToRes = sharedZones?.res ? ((sharedZones.res - livePrice) / livePrice) * 100 : null;

      let shortPainScore = 0;
      if (isShortTrapped) {
        if (useSpotData) {
          shortPainScore += Math.min(Math.abs(shortDistPct) * 10, 30); 
          if (oiDeltaPct > 0) shortPainScore += Math.min(oiDeltaPct * 2, 20); 
          if (frLive < -0.0001) shortPainScore += 20; 
          if (spotCvd > 0) shortPainScore += 30; 
        } else {
          shortPainScore += Math.min(Math.abs(shortDistPct) * 15, 40); 
          if (oiDeltaPct > 0) shortPainScore += Math.min(oiDeltaPct * 3, 30); 
          if (frLive < -0.0001) shortPainScore += 30; 
        }
      }

      setSqueezeData({
        livePrice, frLive, spotCvd, timeframeLabel: histTarget.label,
        useSpotDataSnapshot: useSpotData,
        liveOI, histOI, oiDeltaPct,
        longAvg, longDistPct, isLongTrapped, longPainScore: Math.min(Math.round(longPainScore), 100),
        longTotalPnL, longWhalePnL, longRetailPnL, distToSup,
        shortAvg, shortDistPct, isShortTrapped, shortPainScore: Math.min(Math.round(shortPainScore), 100),
        shortTotalPnL, shortWhalePnL, shortRetailPnL, distToRes
      });
      
      setLastScan(new Date().toLocaleTimeString('vi-VN'));
      setIsAnalyzing(false);
    }, 400); 
  };

  const boxStyle = { flex: 1, padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}` };

  return (
    <div style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '16px', marginTop: '24px' }}>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '15px', color: colors.textMain, display: 'flex', alignItems: 'center', gap: '8px' }}>
          🔥 LIQUIDATION & SQUEEZE RADAR
        </h3>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: useSpotData ? colors.green : colors.textSub, borderRight: `1px solid ${colors.border}`, paddingRight: '12px' }}>
            <input 
              type="checkbox" 
              checked={useSpotData} 
              onChange={(e) => setUseSpotData(e.target.checked)} 
              style={{ width: '16px', height: '16px' }}
            />
            {useSpotData ? 'Gộp Data Spot' : 'Chỉ dùng Futures'}
          </label>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: colors.textSub }}>Mốc đối chiếu:</span>
            <select 
              value={baselineId} 
              onChange={(e) => setBaselineId(e.target.value)} 
              style={{ backgroundColor: colors.bg, color: 'white', border: `1px solid ${colors.border}`, borderRadius: '4px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer' }}
            >
              {histDataList && histDataList.length > 0 ? (
                histDataList.map(h => <option key={h.id} value={h.id}>{h.label.toUpperCase()}</option>)
              ) : <option value="4h">4H TRƯỚC (CẦN FETCH)</option>}
            </select>
          </div>

          {lastScan && <span style={{ fontSize: '11px', color: colors.textSub, marginLeft: '4px' }}>Cập nhật: {lastScan}</span>}
          
          <button 
            onClick={analyzeSqueeze} 
            disabled={isAnalyzing}
            style={{ backgroundColor: colors.bg, border: `1px solid ${colors.warn}`, color: colors.warn, padding: '6px 16px', borderRadius: '6px', cursor: isAnalyzing ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '12px' }}
          >
            {isAnalyzing ? '⏳ ĐANG QUÉT...' : '⚡ QUÉT TÌM SQUEEZE'}
          </button>
        </div>
      </div>

      {!squeezeData && !isAnalyzing && (
        <div style={{ textAlign: 'center', color: colors.textSub, padding: '20px', fontSize: '13px' }}>
          Bạn có thể tùy chọn <b>Bật/Tắt dữ liệu Spot</b>, chọn Mốc tham chiếu và bấm <b>QUÉT TÌM SQUEEZE</b>.
        </div>
      )}

      {squeezeData && (
        <>
          <div style={{ fontSize: '12px', color: colors.textSub, marginBottom: '12px', fontStyle: 'italic' }}>
            *Đang dùng mốc đối chiếu O.I {squeezeData.useSpotDataSnapshot ? 'và Spot CVD' : '(Chỉ dùng Phái sinh)'}: <b>[{squeezeData.timeframeLabel.toUpperCase()}]</b>
          </div>

          <div style={{ marginBottom: '20px', backgroundColor: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px', fontWeight: 'bold' }}>
              <span style={{ color: colors.textSub }}>CHỈ SỐ NGUY HIỂM (PAIN METER):</span>
              <span style={{ color: Math.max(squeezeData.longPainScore, squeezeData.shortPainScore) >= 80 ? colors.red : (Math.max(squeezeData.longPainScore, squeezeData.shortPainScore) >= 50 ? colors.warn : colors.green) }}>
                {Math.max(squeezeData.longPainScore, squeezeData.shortPainScore)}/100 
                {Math.max(squeezeData.longPainScore, squeezeData.shortPainScore) >= 60 && (squeezeData.shortPainScore > squeezeData.longPainScore ? ' ⚠️ NGUY CƠ SHORT SQUEEZE' : ' ⚠️ NGUY CƠ LONG SQUEEZE')}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '2px', width: '100%' }}>
              {Array.from({ length: 20 }, (_, i) => {
                const isActive = (i * 5) < Math.max(squeezeData.longPainScore, squeezeData.shortPainScore);
                const barColor = Math.max(squeezeData.longPainScore, squeezeData.shortPainScore) >= 80 ? colors.red : (Math.max(squeezeData.longPainScore, squeezeData.shortPainScore) >= 50 ? colors.warn : colors.green);
                return <div key={i} style={{ flex: 1, height: '14px', backgroundColor: isActive ? barColor : 'rgba(255,255,255,0.05)', borderRadius: '2px', transition: 'all 0.3s' }} />;
              })}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {/* PHE LONG */}
            <div style={{ ...boxStyle, backgroundColor: squeezeData.isLongTrapped ? 'rgba(239, 68, 68, 0.03)' : 'rgba(34, 197, 94, 0.03)', borderColor: squeezeData.isLongTrapped ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)' }}>
              <div style={{ fontWeight: 'bold', color: colors.green, borderBottom: `1px dashed ${colors.border}`, paddingBottom: '8px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                <span>🟢 PHE LONG (BÒ)</span>
                <span style={{ fontSize: '12px', color: squeezeData.isLongTrapped ? colors.red : colors.green }}>{squeezeData.isLongTrapped ? 'Gồng Lỗ (Hoảng loạn)' : 'Nhởn nhơ (Đang lãi)'}</span>
              </div>
              
              <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>📍 <b>Khoảng cách Entry:</b><br/>
                  • Entry TB: <strong style={{color: colors.textMain}}>${formatSmartPrice(squeezeData.longAvg)}</strong><br/>
                  • Giá Live: <strong style={{color: squeezeData.isLongTrapped ? colors.red : colors.green}}>${formatNum(squeezeData.livePrice)} ({squeezeData.longDistPct > 0 ? '+' : ''}{squeezeData.longDistPct.toFixed(2)}%)</strong>
                </div>
                
                <div>💰 <b>PnL Tạm Tính (Dòng Tiền Đang Gồng):</b><br/>
                  • Cá Mập: <strong style={{color: squeezeData.longWhalePnL >= 0 ? colors.green : colors.red}}>{squeezeData.longWhalePnL > 0 ? '+' : ''}{formatUSD(squeezeData.longWhalePnL)}</strong><br/>
                  • Nhỏ lẻ: <strong style={{color: squeezeData.longRetailPnL >= 0 ? colors.green : colors.red}}>{squeezeData.longRetailPnL > 0 ? '+' : ''}{formatUSD(squeezeData.longRetailPnL)}</strong>
                </div>

                <div>⚔️ <b>Hành vi O.I (Độ cố chấp):</b><br/>
                  • Tăng/Giảm: <strong style={{color: (squeezeData.oiDeltaPct > 2 && squeezeData.isLongTrapped) ? colors.red : colors.textMain}}>{squeezeData.oiDeltaPct > 0 ? '+' : ''}{squeezeData.oiDeltaPct.toFixed(2)}%</strong> 
                  {squeezeData.oiDeltaPct > 2 && squeezeData.isLongTrapped && <span style={{color: colors.red}}> ↳ Cố chấp DCA!</span>}
                </div>

                <div>🚪 <b>VÙNG SÁT SINH (TRAP DOOR):</b><br/>
                  {sharedZones?.isScanned ? (
                    <>
                      • Hỗ trợ (Cắt lỗ): <strong style={{color: colors.textMain}}>{sharedZones.sup ? `$${formatNum(sharedZones.sup)}` : 'N/A'}</strong><br/>
                      • Cách Hỗ trợ: <strong style={{color: squeezeData.distToSup < 1.0 ? colors.red : colors.green}}>{squeezeData.distToSup ? `-${squeezeData.distToSup.toFixed(2)}%` : 'N/A'}</strong> {squeezeData.distToSup < 1.0 && '⚠️ CỰC KỲ NGUY HIỂM'}
                    </>
                  ) : <span style={{color: colors.warn, fontStyle: 'italic'}}>⚠️ Chưa Quét Radar Cản ở bảng trên!</span>}
                </div>
              </div>
            </div>

            {/* PHE SHORT */}
            <div style={{ ...boxStyle, backgroundColor: squeezeData.isShortTrapped ? 'rgba(239, 68, 68, 0.03)' : 'rgba(34, 197, 94, 0.03)', borderColor: squeezeData.isShortTrapped ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)' }}>
              <div style={{ fontWeight: 'bold', color: colors.red, borderBottom: `1px dashed ${colors.border}`, paddingBottom: '8px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                <span>🔴 PHE SHORT (GẤU)</span>
                <span style={{ fontSize: '12px', color: squeezeData.isShortTrapped ? colors.red : colors.green }}>{squeezeData.isShortTrapped ? 'Gồng Lỗ (Hoảng loạn)' : 'Nhởn nhơ (Đang lãi)'}</span>
              </div>
              
              <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>📍 <b>Khoảng cách Entry:</b><br/>
                  • Entry TB: <strong style={{color: colors.textMain}}>${formatSmartPrice(squeezeData.shortAvg)}</strong><br/>
                  • Giá Live: <strong style={{color: squeezeData.isShortTrapped ? colors.red : colors.green}}>${formatSmartPrice(squeezeData.livePrice)} ({squeezeData.shortDistPct > 0 ? '+' : ''}{squeezeData.shortDistPct.toFixed(2)}%)</strong>
                </div>

                <div>💰 <b>PnL Tạm Tính (Dòng Tiền Đang Gồng):</b><br/>
                  • Cá Mập: <strong style={{color: squeezeData.shortWhalePnL >= 0 ? colors.green : colors.red}}>{squeezeData.shortWhalePnL > 0 ? '+' : ''}{formatUSD(squeezeData.shortWhalePnL)}</strong><br/>
                  • Nhỏ lẻ: <strong style={{color: squeezeData.shortRetailPnL >= 0 ? colors.green : colors.red}}>{squeezeData.shortRetailPnL > 0 ? '+' : ''}{formatUSD(squeezeData.shortRetailPnL)}</strong>
                </div>

                <div>⚔️ <b>Hành vi O.I (Độ cố chấp):</b><br/>
                  • Tăng/Giảm: <strong style={{color: (squeezeData.oiDeltaPct > 2 && squeezeData.isShortTrapped) ? colors.red : colors.textMain}}>{squeezeData.oiDeltaPct > 0 ? '+' : ''}{squeezeData.oiDeltaPct.toFixed(2)}%</strong> 
                  {squeezeData.oiDeltaPct > 2 && squeezeData.isShortTrapped && <span style={{color: colors.red}}> ↳ Cố chấp DCA!</span>}
                </div>

                <div>🚪 <b>VÙNG SÁT SINH (TRAP DOOR):</b><br/>
                  {sharedZones?.isScanned ? (
                    <>
                      • Kháng cự (Cháy): <strong style={{color: colors.textMain}}>{sharedZones.res ? `$${formatSmartPrice(sharedZones.res)}` : 'N/A'}</strong><br/>
                      • Cách Kháng cự: <strong style={{color: squeezeData.distToRes < 1.0 ? colors.red : colors.green}}>{squeezeData.distToRes ? `+${squeezeData.distToRes.toFixed(2)}%` : 'N/A'}</strong> {squeezeData.distToRes < 1.0 && '⚠️ SẮP ĐỤNG TRẦN'}
                    </>
                  ) : <span style={{color: colors.warn, fontStyle: 'italic'}}>⚠️ Chưa Quét Radar Cản ở bảng trên!</span>}
                </div>
              </div>
            </div>

          </div>

          {/* BỘ NÃO AI KẾT LUẬN */}
          <div style={{ marginTop: '20px', padding: '16px', backgroundColor: Math.max(squeezeData.longPainScore, squeezeData.shortPainScore) >= 60 ? 'rgba(234, 179, 8, 0.1)' : 'rgba(255,255,255,0.03)', borderRadius: '8px', borderLeft: `4px solid ${Math.max(squeezeData.longPainScore, squeezeData.shortPainScore) >= 60 ? colors.warn : colors.green}` }}>
            <div style={{ fontSize: '13px', color: colors.textMain, marginBottom: '8px', lineHeight: '1.6' }}>
              <strong>🚨 GÓC PHÂN TÍCH:</strong><br/>
              <span style={{ color: colors.textSub }}>
                {Math.max(squeezeData.longPainScore, squeezeData.shortPainScore) < 50 ? "Tâm lý bầy đàn đang ở mức ổn định. Không có phe nào gồng lỗ đến mức cực hạn." : 
                  (squeezeData.shortPainScore > squeezeData.longPainScore 
                    ? `Phe SHORT đang rơi vào thế chân tường! Lỗ nặng (${squeezeData.shortDistPct.toFixed(2)}%), nhưng vẫn cố nạp tiền gồng (O.I thay đổi ${squeezeData.oiDeltaPct.toFixed(1)}%).${squeezeData.useSpotDataSnapshot ? ` Dòng tiền Spot đang MUA VÀO (${formatUSD(squeezeData.spotCvd)}) đỡ giá, không cho sập.` : ''}` 
                    : `Phe LONG đang bị luộc chín! Lỗ mòn mỏi (${squeezeData.longDistPct.toFixed(2)}%), nhưng vẫn DCA gồng (O.I thay đổi ${squeezeData.oiDeltaPct.toFixed(1)}%).${squeezeData.useSpotDataSnapshot ? ` Dòng tiền Spot đang TÁNG HÀNG XẢ XUỐNG (${formatUSD(squeezeData.spotCvd)}).` : ''}`
                  )
                }
              </span>
            </div>
            <div style={{ fontSize: '13px', color: colors.textMain }}>
              <strong>🎯 HÀNH ĐỘNG:</strong> <span style={{ color: Math.max(squeezeData.longPainScore, squeezeData.shortPainScore) >= 60 ? colors.warn : colors.green }}>
                {Math.max(squeezeData.longPainScore, squeezeData.shortPainScore) < 50 ? "⚖️ Thị trường cân bằng, quan sát thêm." :
                  (squeezeData.shortPainScore > squeezeData.longPainScore
                    ? "Ngay khi giá phá vỡ Kháng cự gần nhất, đội Short sẽ bị cháy (Short Squeeze). Canh sẵn BUY STOP/LONG."
                    : "Ngay khi thủng Hỗ trợ gần nhất, đội Long sẽ giẫm đạp cắt lỗ (Long Squeeze). Canh sẵn SELL STOP/SHORT."
                  )
                }
              </span>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
